import { Button, Table } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components/macro'
import FadeIn from '../../components/styled/FadeIn'
import { fetchStatementOrders } from 'src/features/OrdersSlice'
import DownloadStatementCsv from './ DownloadStatementCsv'
import { RESPONSIVE_TABLE_SCROLL } from 'src/utils/responsiveTable'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import { formatGbp } from 'src/utils/formatGbp'

const Statement = venueId => {
  const blocked = useIsBlocked('statement')

  if (blocked) {
    return <PermissionBlockScreen />
  }

  const statementOrders = useSelector(
    state => state.orders.filteredStatementOrders,
  )

  const dispatch = useDispatch()
  const orderFilter = useSelector(state => state.filter.orders)
  const { startDate, endDate, suppliers, types } = orderFilter
  const customStartDate = startDate?.split('/')?.reverse()?.join('-')
  const customEndDate = endDate?.split('/')?.reverse()?.join('-')

  useEffect(() => {
    dispatch(
      fetchStatementOrders(
        venueId.venueId,
        startDate,
        endDate,
        suppliers,
        types,
      ),
    )
  }, [venueId, startDate, endDate, suppliers, types])

  const isStatementEmpty = !statementOrders || statementOrders.length === 0

  return (
    <Wrapper>
      {isStatementEmpty && (
        <EmptyHint>
          <strong>No purchasing data for this period.</strong> The statement
          lists orders placed in the selected date range (by order date) that
          were confirmed and not rejected. If you expected purchases here, widen
          the date filter above — it defaults to the last 31 days — or check the
          supplier filter. Unapproved or rejected orders are intentionally
          excluded.
        </EmptyHint>
      )}
      <ButtonRow>
        <Link
          to={`/v/${venueId.venueId}/invoice/${customStartDate}/${customEndDate}`}
        >
          <Button>View statement</Button>
        </Link>
        <DownloadStatementCsv />
      </ButtonRow>
      <Table
        pagination={{
          position: 'both',
          hideOnSinglePage: true,
          showSizeChanger: false,
        }}
        scroll={RESPONSIVE_TABLE_SCROLL}
        rowKey={record =>
          `${record?._id?.supplierId}-${record?._id?.purchaseOrderNumber}`
        }
        dataSource={statementOrders ? statementOrders : []}
        columns={[
          {
            title: 'Supplier title',
            dataIndex: '_id.supplierName',
            key: '_id.supplierName',
          },
          {
            title: 'PO Number',
            dataIndex: '_id.purchaseOrderNumber',
            key: '_id.purchaseOrderNumber',
          },
          {
            title: 'Date of the order',
            dataIndex: 'orderedDate',
            key: 'orderedDate',
            render: item => dayjs(item).format('DD/MM/YY'),
          },
          {
            title: 'Value of the order',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: item => formatGbp(item),
          },
          {
            title: 'Value of VAT',
            dataIndex: 'totalVat',
            key: 'totalVat',
            render: item => formatGbp(item),
          },
          {
            title: 'Total due',
            dataIndex: 'totalAmountWithVat',
            key: 'totalAmountWithVat',
            render: item => formatGbp(item),
          },
        ]}
        bordered
        footer={() => {
          const totalAmount = statementOrders?.reduce(
            (sum, item) => sum + (item.totalAmount || 0),
            0,
          )
          const totalVat = statementOrders?.reduce(
            (sum, item) => sum + (item.totalVat || 0),
            0,
          )
          const totalWithVat = statementOrders?.reduce(
            (sum, item) => sum + (item.totalAmountWithVat || 0),
            0,
          )

          return (
            <div
              style={{
                textAlign: 'right',
                paddingRight: 16,
                fontWeight: 'bold',
              }}
            >
              <div>Statement total (ex VAT): {formatGbp(totalAmount)}</div>
              <div>Total VAT: {formatGbp(totalVat)}</div>
              <div>Total due (inc VAT): {formatGbp(totalWithVat)}</div>
            </div>
          )
        }}
      />
    </Wrapper>
  )
}

const ButtonRow = styled.div`
  margin-top: 16px;
  margin-bottom: 18px;
`

const Wrapper = styled(FadeIn)`
  width: 100%;
`

const EmptyHint = styled.div`
  margin-top: 16px;
  margin-bottom: 8px;
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.5;
  color: #595959;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
`

export default Statement
